import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Typography, Button, IconButton, Modal, TextField, Avatar, Chip, FormControl, Select, MenuItem, InputAdornment} from '@mui/material'
import {Close, DateRange, AccessTime, Search} from '@mui/icons-material'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {scheduleApi, clientApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {SkeletonContainer, FieldSkeleton, VisitDialogCardSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [caregivers, setCaregivers] = useState([])
    const [selectedCaregivers, setSelectedCaregivers] = useState([])
    const [searchCaregiverTerms, setSearchCaregiverTerms] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)

    // Every open/close bumps the id, so a response for an earlier opening can't preselect its caregiver here.
    useEffect(() => {
        const id = ++requestId.current
        if (open && !loading && items?.length > 0) {
            setSkeleton(true)
            clientApi
                .getClientCaregivers(branchId, items?.[0]?.schedule?.client_id)
                .then(({data: {caregivers: data}}) => {
                    if (id !== requestId.current) return
                    setSkeleton(false)
                    setCaregivers(
                        data?.map(item => ({
                            ...item?.caregiver,
                            display_name: item?.caregiver.first_name + ' ' + item?.caregiver.last_name,
                        })),
                    )
                    setSelectedCaregivers(items?.map(item => item?.caregiver_id))
                })
                .catch(error => {
                    if (id !== requestId.current) return
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [open, loading])

    const submit = () => {
        setLoading(true)
        scheduleApi
            .changeCaregiver(branchId, items?.[0]?.schedule_id, {
                visits: items?.map((item, index) => ({visit_id: item?.id, caregiver_id: selectedCaregivers[index]})),
            })
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Caregiver Reassigned Successfully!',
                        description: `The visit for ${items?.[0]?.schedule?.client?.first_name} on ${format(new Date(ignoreTimezone(items?.[0]?.date)), 'EEE, dd MMM yyyy')} has been reassigned to ${caregivers.find(item => item.id === selectedCaregivers[0])?.first_name}.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Caregiver Reassigned Successfully!',
                        description: `The ${items?.length} schedules have changed the caregiver.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const caregiverChange = (e, index) => {
        setSelectedCaregivers(prev => {
            const newTerms = [...prev]
            newTerms[index] = e.target.value
            return newTerms
        })
    }

    const renderCaregiverLabel = selected => {
        const caregiver = caregivers.find(item => item.id === selected)
        return caregiver ? caregiver.display_name : 'Search or select a caregiver'
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Reassign Caregiver
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={submit} disabled={skeleton || loading} style={{flex: 1}}>
                                    Confirm Reassignment {items?.length > 1 ? `(${items?.length})` : ''}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Reassign Caregiver
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        You are about to assign a new caregiver for this visit.
                    </Typography>
                    {items.map((item, index) =>
                        loading ? (
                            <div key={index}>
                                <br />
                                <VisitDialogCardSkeleton section="changeCaregiver" />
                            </div>
                        ) : (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                    <div className="form">
                                        <div className="form-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Assigned Caregiver
                                            </Typography>
                                            <div className="people-card" style={{marginTop: -15}}>
                                                {item?.caregiver?.avatar_url ? (
                                                    <Avatar src={item?.caregiver?.avatar_url} style={{width: 24, height: 24}} />
                                                ) : (
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: getColorFromName(
                                                                item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name,
                                                            ),
                                                            width: 24,
                                                            height: 24,
                                                            fontSize: 12,
                                                        }}>
                                                        {getInitials(item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name)}
                                                    </Avatar>
                                                )}
                                                <Typography variant="body1" color="text.primary">
                                                    {item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="form-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Select New Caregiver
                                            </Typography>
                                            {skeleton && (
                                                <SkeletonContainer>
                                                    <FieldSkeleton label={false} height={40} sx={{mb: '6px'}} />
                                                </SkeletonContainer>
                                            )}
                                            <FormControl fullWidth style={{marginBottom: 6, display: skeleton ? 'none' : undefined}}>
                                                <Select
                                                    value={selectedCaregivers?.[index]}
                                                    onChange={e => caregiverChange(e, index)}
                                                    renderValue={renderCaregiverLabel}
                                                    size="small"
                                                    displayEmpty
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: {
                                                                maxHeight: 300,
                                                                overflow: 'auto',
                                                            },
                                                        },
                                                    }}>
                                                    <MenuItem disableRipple disableTouchRipple>
                                                        <TextField
                                                            placeholder="Search caregiver name..."
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            value={searchCaregiverTerms?.[index]}
                                                            onChange={e =>
                                                                setSearchCaregiverTerms(prev => {
                                                                    const newTerms = [...prev]
                                                                    newTerms[index] = e.target.value
                                                                    return newTerms
                                                                })
                                                            }
                                                            onClick={e => e.stopPropagation()}
                                                            onKeyDown={e => e.stopPropagation()}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <Search />
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </MenuItem>
                                                    {caregivers
                                                        ?.filter(
                                                            item =>
                                                                !searchCaregiverTerms?.[index] ||
                                                                item?.display_name
                                                                    ?.toLowerCase()
                                                                    .includes(searchCaregiverTerms?.[index]?.toLowerCase()),
                                                        )
                                                        ?.map(item => (
                                                            <MenuItem key={item.id} value={item.id}>
                                                                <div className="people-card">
                                                                    {item?.avatar_url ? (
                                                                        <Avatar src={item?.avatar_url} style={{width: 32, height: 32}} />
                                                                    ) : (
                                                                        <Avatar
                                                                            style={{
                                                                                backgroundColor: getColorFromName(
                                                                                    item?.first_name + ' ' + item?.last_name,
                                                                                ),
                                                                                width: 32,
                                                                                height: 32,
                                                                                fontSize: 16,
                                                                            }}>
                                                                            {getInitials(item?.first_name + ' ' + item?.last_name)}
                                                                        </Avatar>
                                                                    )}
                                                                    <Typography variant="body2" color="text.primary">
                                                                        {item?.first_name + ' ' + item?.last_name}
                                                                    </Typography>
                                                                </div>
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <br />
                                </div>
                            </div>
                        ),
                    )}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={submit} disabled={skeleton || loading}>
                                Confirm Reassignment {items?.length > 1 ? `(${items?.length})` : ''}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
